<script setup lang="ts">
import { useTemplateRef } from "vue";
import { z } from "zod";
import { UiFormBuilder, UiButton, PRICE_TYPE } from "@shopexperts/shared";
import type { BuilderRowConfig } from "@shopexperts/shared";
import type { PackagedService } from "./types/listingModels";
import type { UiFileUploadItem } from "@shopexperts/shared/dist/types/ui/UiFileUpload.vue";
import type { PriceType } from "@shopexperts/shared";
import { useMediaUploadIntegration } from "./composables/useMediaUploadIntegration";

const priceTypeOptions = [
  { value: PRICE_TYPE.FIXED, label: 'Fixed' },
  { value: PRICE_TYPE.MONTHLY, label: 'Monthly' },
];

const props = defineProps<{ submitLabel?: string }>();

const emit = defineEmits<{
  (e: 'submit', value: PackagedService): void;
}>();

const { uploadFn, deleteFn } = useMediaUploadIntegration();

const schema = z.object({
  images: z.array(z.any()).max(1).optional(),
  galleryImages: z.array(z.any()).max(3).optional(),
  serviceName: z.string().min(1, "Service name is required"),
  price: z.number("Price is required").min(1, "Price should be greater than 0"),
  priceType: z.enum([PRICE_TYPE.FIXED, PRICE_TYPE.MONTHLY]),
  description: z.string().optional(),
});

const rows: BuilderRowConfig[] = [
  {
    columns: [
      { 
        name: 'images', 
        type: 'file-upload', 
        label: 'Packaged Service Thumbnail', 
        props: { 
          primaryText: 'Packaged Service Thumbnail', 
          secondaryText: 'Upload a thumbnail image to make your service stand out.\n Recommended aspect ratio: 16:9.', 
          accept: 'image/*',
          maxFiles: 1,
          autoUpload: true,
          uploadFn,
          deleteFn,
          collection: 'packaged_service_featured',
        } 
      },
    ],
  },
  {
    columns: [
      {
        name: 'galleryImages',
        type: 'file-upload',
        label: 'Gallery Images',
        props: {
          primaryText: 'Gallery Images',
          secondaryText: 'Add up to 3 images to showcase your service. Recommended aspect ratio: 16:9.',
          accept: 'image/*',
          maxFiles: 3,
          aspectRatio: '16:9',
          autoUpload: true,
          uploadFn,
          deleteFn,
          collection: 'packaged_service_gallery',
        }
      },
    ],
  },
  {
    columns: [
      { name: 'serviceName', type: 'text', label: 'Packaged Service Name', hint: 'Use a clear, descriptive name that clients will recognize instantly.', props: { placeholder: 'Packaged Service Name' } },
    ],
  },
  {
    columns: [
      { name: 'price', type: 'currency', label: 'Starting Price (USD)', hint: 'Set a starting price for this service. You can update it later.', props: { placeholder: '$0.00' } },
      { 
        name: 'priceType', 
        type: 'select', 
        label: 'Price Type', 
        props: { 
          placeholder: 'Select price type',
          options: priceTypeOptions,
        } 
      },
    ],
  },
  {
    columns: [
      { name: 'description', type: 'richtext', label: 'Short Description', hint: 'Provide a brief description of what clients will receive with this packaged service.', props: { placeholder: 'Describe your packaged service...' } },
    ],
  },
];

/** TODO: Why is this using inline type? */
const model = defineModel<{ 
  images?: UiFileUploadItem[]; 
  galleryImages?: UiFileUploadItem[];
  serviceName: string; 
  price: number; 
  priceType: PriceType;
  description?: string;
}>({ default: { serviceName: '', price: 0, priceType: PRICE_TYPE.FIXED, description: '' } });

const formRef = useTemplateRef('formRef');
defineExpose({
  resetForm: () => formRef.value?.resetForm(),
});

function onSubmit(values: z.infer<typeof schema>) {
  const serviceName = String(values.serviceName || "");
  const price = values.price;
  const thumbnail = values.images?.[0] || undefined;
  const galleryImages = values.galleryImages || [];
  const priceType = values.priceType || PRICE_TYPE.FIXED;

  const payload: PackagedService = {
    id: null,
    service_name: serviceName,
    thumbnail: thumbnail,
    gallery_images: galleryImages,
    starting_price: Number(price) || 0,
    price_type: priceType,
    description: values.description || '',
  };
  console.log(`[PackagedServiceForm] onSubmit`, payload);
  emit('submit', payload);
}
</script>

<template>
  <UiFormBuilder ref="formRef" :schema="schema" :rows="rows" scroll-to-first-error v-model="model" @submit="onSubmit">
    <template #actions>
      <UiButton type="submit" variant="primary" :label="props.submitLabel || 'Add Packaged Service'" />
    </template>
  </UiFormBuilder>
</template>
